import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Flex } from 'design-system-web';
import { WorkOrderModuleCategoriesSettings } from 'lane-shared/domains/workOrder';
import { Assignable } from 'domains-work-order-type-contracts';
import {
  buildLockedCategoryItems,
  buildLockedIssueTypeItems,
  getIssueTypeInfoKey,
} from './coverageHoursPanelUtils';
import { useCoverageHoursPanelOptions } from './useCoverageHoursPanelOptions';
import { TeamCoverageEntry } from './coverageHoursTeamUtils';
import {
  CoverageHoursCategoryOption,
  CoverageHoursIssueTypeOption,
} from './useCoverageHoursOptions';
import { IssueTypeInfoMessage } from './IssueTypeInfoMessage';
import { TeamCoverageCard } from './TeamCoverageCard';
import { AddTeamForm } from './AddTeamForm';
import styles from './CoverageHoursPanel.scss';

type FormMode = 'idle' | 'adding' | { editingId: string };

export type CoverageHoursFormProps = {
  mode: 'create' | 'edit';
  categories: WorkOrderModuleCategoriesSettings[];
  categoryOptions: CoverageHoursCategoryOption[];
  issueTypeOptions: CoverageHoursIssueTypeOption[];
  categoryValue: string | null;
  onCategoryChange: (value: string) => void;
  issueTypeValue: string;
  onIssueTypeChange: (value: string) => void;
  assignableTeams: Assignable[];
  savedTeams: TeamCoverageEntry[];
  onSaveTeam: (entry: TeamCoverageEntry) => void;
  onDiscardTeam: (id: string) => void;
  channelId: string;
  onConflictChange: (hasConflict: boolean) => void;
};

export function CoverageHoursForm({
  mode,
  categories,
  categoryOptions,
  issueTypeOptions,
  categoryValue,
  onCategoryChange,
  issueTypeValue,
  onIssueTypeChange,
  assignableTeams,
  savedTeams,
  onSaveTeam,
  onDiscardTeam,
  channelId,
  onConflictChange,
}: CoverageHoursFormProps) {
  const { t } = useTranslation();
  const { categoryItems, issueTypeItems } = useCoverageHoursPanelOptions(
    categories,
    categoryValue,
    categoryOptions,
    issueTypeOptions
  );
  const [formMode, setFormMode] = useState<FormMode>('idle');

  const isEditing = mode === 'edit';
  const isFormOpen = formMode !== 'idle';
  const issueTypeInfoKey = getIssueTypeInfoKey(issueTypeValue);
  const showInfoMessage = !isFormOpen && savedTeams.length === 0;

  const handleTeamDone = (entry: TeamCoverageEntry) => {
    onSaveTeam(entry);
    setFormMode('idle');
  };

  return (
    <Flex direction="column" gap={4} className={styles.panelContent}>
      <div className={styles.categoryRow}>
        <Dropdown
          id="coverage-hours-category-select"
          testId="coverage-hours-category-dropdown"
          doTranslation={false}
          isFullWidth
          items={
            isEditing ? buildLockedCategoryItems(categoryValue) : categoryItems
          }
          value={categoryValue}
          onChange={item => onCategoryChange(String(item.value))}
          label={t(
            'web.admin.serviceRequest.settings.coverageHours.create.category'
          )}
          placeholder={t(
            'web.admin.serviceRequest.settings.coverageHours.create.categoryPlaceholder'
          )}
          fixedLabel
          isRequired
          disabled={isEditing}
          menuPosition="fixed"
        />

        {categoryValue && (
          <Dropdown
            id="coverage-hours-issue-type-select"
            testId="coverage-hours-issue-type-dropdown"
            doTranslation={false}
            isFullWidth
            items={
              isEditing
                ? buildLockedIssueTypeItems(issueTypeValue, t)
                : issueTypeItems
            }
            value={issueTypeValue}
            onChange={item => onIssueTypeChange(String(item.value))}
            label={t(
              'web.admin.serviceRequest.settings.coverageHours.create.issueType'
            )}
            fixedLabel
            disabled={isEditing}
            menuPosition="fixed"
          />
        )}
      </div>

      {categoryValue && (
        <>
          {showInfoMessage && issueTypeInfoKey && (
            <IssueTypeInfoMessage
              infoKey={issueTypeInfoKey}
              categoryValue={categoryValue}
            />
          )}

          {savedTeams.map(entry =>
            typeof formMode === 'object' && formMode.editingId === entry.id ? (
              <AddTeamForm
                key={entry.id}
                assignableTeams={assignableTeams}
                initialEntry={entry}
                onDone={handleTeamDone}
                onDiscard={() => setFormMode('idle')}
                savedTeams={savedTeams}
                editingEntryId={entry.id}
                channelId={channelId}
                category={categoryValue}
                issue={issueTypeValue}
                onConflictChange={onConflictChange}
              />
            ) : (
              <TeamCoverageCard
                key={entry.id}
                entry={entry}
                onEdit={() => setFormMode({ editingId: entry.id })}
                onDiscard={() => onDiscardTeam(entry.id)}
                disabled={isFormOpen}
              />
            )
          )}

          {formMode === 'adding' && (
            <AddTeamForm
              assignableTeams={assignableTeams}
              onDone={handleTeamDone}
              onDiscard={() => setFormMode('idle')}
              savedTeams={savedTeams}
              channelId={channelId}
              category={categoryValue}
              issue={issueTypeValue}
              onConflictChange={onConflictChange}
            />
          )}

          <Flex>
            <Button
              variant="secondary"
              size="large"
              testId="coverage-hours-add-team"
              disabled={isFormOpen}
              onClick={() => setFormMode('adding')}
            >
              {t(
                'web.admin.serviceRequest.settings.coverageHours.create.addTeam'
              )}
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  );
}
